import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, Bell, Gift, Share2, Printer,
  RefreshCw, Building, CreditCard, Megaphone, Users, CheckCircle, XCircle, Save,
} from 'lucide-react';

/**
 * Tab "preise" – ausgelagerte Tab-Komponente.
 * Erhält alle benötigten State- und Handler-Referenzen vom Parent via Props.
 */
const SettingsPreise = ({ ctx }) => {
  const { t } = useTranslation();
  const {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
  } = ctx;

  return (
    <>

          {/* Pricing & Business Rules */}
          <Card className="card-shadow mb-6" data-testid="pricing-rules-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-[hsl(var(--primary))]" />
                Preise & Geschäftsregeln
              </CardTitle>
              <CardDescription>Regionale Rabatte, Express-Zuschlaege und Vereins-Konditionen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>ZO-Firmen Rabatt (%)</Label>
                  <Input type="number" min="0" max="100" value={settings.zo_discount_percent}
                    onChange={(e) => setSettings({...settings, zo_discount_percent: parseFloat(e.target.value) || 0})}
                    data-testid="zo-discount-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Rabatt für verifizierte ZO-Firmen</p>
                </div>
                <div>
                  <Label>Vereins-Gratis-Wochen/Jahr</Label>
                  <Input type="number" min="0" max="52" value={settings.verein_free_weeks}
                    onChange={(e) => setSettings({...settings, verein_free_weeks: parseInt(e.target.value) || 0})}
                    data-testid="verein-weeks-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Kostenlose Wochen pro Jahr für Vereine</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Express-Zuschlag (%)</Label>
                  <Input type="number" min="0" max="100" value={settings.express_surcharge_percent}
                    onChange={(e) => setSettings({...settings, express_surcharge_percent: parseFloat(e.target.value) || 0})}
                    data-testid="express-surcharge-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Aufschlag bei kurzfristigen Buchungen</p>
                </div>
                <div>
                  <Label>Express-Schwelle (Tage)</Label>
                  <Input type="number" min="1" max="30" value={settings.express_threshold_days}
                    onChange={(e) => setSettings({...settings, express_threshold_days: parseInt(e.target.value) || 7})}
                    data-testid="express-threshold-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Buchung weniger als X Tage im Voraus = Express</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Setup-Gebühr (CHF)</Label>
                  <Input type="number" min="0" step="10" value={settings.setup_fee}
                    onChange={(e) => setSettings({...settings, setup_fee: parseFloat(e.target.value) || 0})}
                    data-testid="setup-fee-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Einmalige Gebühr bei kurzer Laufzeit</p>
                </div>
                <div>
                  <Label>Setup-Schwelle (Wochen)</Label>
                  <Input type="number" min="1" max="8" value={settings.setup_fee_threshold_weeks}
                    onChange={(e) => setSettings({...settings, setup_fee_threshold_weeks: parseInt(e.target.value) || 2})}
                    data-testid="setup-threshold-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Setup-Gebühr bei Laufzeit unter X Wochen</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Mindestzeit Druckarbeiten (Tage)</Label>
                  <Input type="number" min="1" max="30" value={settings.min_print_lead_days || 7}
                    onChange={(e) => setSettings({...settings, min_print_lead_days: parseInt(e.target.value) || 7})}
                    data-testid="min-print-lead-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Mindestvorlauf wenn Druck noch erstellt werden muss</p>
                </div>
                <div>
                  <Label>Empfehlungs-Anteil für Vereine (%)</Label>
                  <Input type="number" min="0" max="100" value={settings.referral_verein_share_percent}
                    onChange={(e) => setSettings({...settings, referral_verein_share_percent: parseFloat(e.target.value) || 0})}
                    className="max-w-xs"
                    data-testid="referral-verein-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Anteil der Empfehlungs-Provision für Vereine</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle>Buchungseinstellungen</CardTitle>
              <CardDescription>
                Legen Sie fest, ob Buchungen nach Zahlungseingang automatisch oder manuell best&auml;tigt werden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Buchungsbest&auml;tigung</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'booking_confirmation_mode', value: 'automatic' } })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      settings.booking_confirmation_mode === 'automatic'
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.3)]'
                    }`}
                    data-testid="settings-confirmation-automatic"
                  >
                    <p className="font-semibold text-sm mb-1">Automatisch</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Buchung wird nach Zahlung sofort best&auml;tigt. Ideal wenn alles standardisiert abl&auml;uft.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'booking_confirmation_mode', value: 'manual' } })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      settings.booking_confirmation_mode === 'manual'
                        ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
                        : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary)/0.3)]'
                    }`}
                    data-testid="settings-confirmation-manual"
                  >
                    <p className="font-semibold text-sm mb-1">Manuell</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Nach Zahlung muss ein Admin die Buchung manuell best&auml;tigen. F&uuml;r Abkl&auml;rungen n&ouml;tig.
                    </p>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cancellation Policy Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle>Stornierungsrichtlinien</CardTitle>
              <CardDescription>
                Konfigurieren Sie die Erstattungsregeln bei Stornierung durch den Kunden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Stornierungsrichtlinie aktivieren</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Wenn deaktiviert, erhalten Kunden bei Stornierung immer eine volle Rückerstattung.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'cancellation_policy_enabled', value: !settings.cancellation_policy_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.cancellation_policy_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-cancellation-policy-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.cancellation_policy_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {settings.cancellation_policy_enabled && (
                <div className="space-y-3 pt-3 border-t border-[hsl(var(--border))]">
                  <Label className="text-sm font-medium block">Erstattungsstufen</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
                    Definieren Sie, wie viel Prozent der Kunde bei Stornierung X Tage vor Buchungsbeginn erstattet bekommt.
                  </p>
                  {(settings.cancellation_tiers || []).map((tier, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-[hsl(var(--muted-foreground))] whitespace-nowrap">Ab</span>
                        <Input
                          type="number"
                          min="0"
                          value={tier.days_before}
                          onChange={(e) => {
                            const newTiers = [...(settings.cancellation_tiers || [])];
                            newTiers[index] = { ...newTiers[index], days_before: parseInt(e.target.value) || 0 };
                            handleInputChange({ target: { name: 'cancellation_tiers', value: newTiers } });
                          }}
                          className="w-20"
                          data-testid={`cancellation-tier-days-${index}`}
                        />
                        <span className="text-sm text-[hsl(var(--muted-foreground))] whitespace-nowrap">Tage vorher:</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={tier.refund_percent}
                          onChange={(e) => {
                            const newTiers = [...(settings.cancellation_tiers || [])];
                            newTiers[index] = { ...newTiers[index], refund_percent: parseInt(e.target.value) || 0 };
                            handleInputChange({ target: { name: 'cancellation_tiers', value: newTiers } });
                          }}
                          className="w-20"
                          data-testid={`cancellation-tier-percent-${index}`}
                        />
                        <span className="text-sm text-[hsl(var(--muted-foreground))]">%</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 px-2"
                          onClick={() => {
                            const newTiers = (settings.cancellation_tiers || []).filter((_, i) => i !== index);
                            handleInputChange({ target: { name: 'cancellation_tiers', value: newTiers } });
                          }}
                          data-testid={`cancellation-tier-remove-${index}`}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newTiers = [...(settings.cancellation_tiers || []), { days_before: 0, refund_percent: 0 }];
                      handleInputChange({ target: { name: 'cancellation_tiers', value: newTiers } });
                    }}
                    data-testid="cancellation-tier-add"
                  >
                    + Stufe hinzufügen
                  </Button>

                  {/* Visual timeline preview */}
                  {(settings.cancellation_tiers || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                      <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-3">Vorschau der Stornierungsstaffel:</p>
                      <div className="relative">
                        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-[hsl(var(--border))]" />
                        {[...(settings.cancellation_tiers || [])]
                          .sort((a, b) => b.days_before - a.days_before)
                          .map((tier, i) => (
                            <div key={i} className="flex items-center gap-3 mb-3 relative pl-7">
                              <div className={`absolute left-1.5 w-3.5 h-3.5 rounded-full border-2 ${
                                tier.refund_percent === 100 ? 'bg-emerald-500 border-emerald-500' :
                                tier.refund_percent === 0 ? 'bg-red-500 border-red-500' :
                                'bg-amber-500 border-amber-500'
                              }`} />
                              <div className="flex items-center justify-between flex-1 p-2 rounded-md bg-[hsl(var(--surface-2))]">
                                <span className="text-sm">Ab <strong>{tier.days_before} Tage</strong> vor Start</span>
                                <span className={`text-sm font-semibold ${
                                  tier.refund_percent === 100 ? 'text-emerald-600' :
                                  tier.refund_percent === 0 ? 'text-red-600' :
                                  'text-amber-600'
                                }`}>{tier.refund_percent}% Erstattung</span>
                              </div>
                            </div>
                          ))}
                        <div className="flex items-center gap-3 relative pl-7">
                          <div className="absolute left-1.5 w-3.5 h-3.5 rounded-full border-2 bg-red-500 border-red-500" />
                          <div className="flex items-center justify-between flex-1 p-2 rounded-md bg-red-50">
                            <span className="text-sm">Darunter</span>
                            <span className="text-sm font-semibold text-red-600">0% Erstattung</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reminder Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Buchungserinnerungen
              </CardTitle>
              <CardDescription>
                Automatische E-Mail-Erinnerungen an Kunden vor Buchungsbeginn.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Erinnerungen aktivieren</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Kunden werden automatisch per E-Mail an bevorstehende Buchungen erinnert.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'reminder_enabled', value: !settings.reminder_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.reminder_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-reminder-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.reminder_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {settings.reminder_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))]">
                  <Label className="text-sm font-medium block mb-2">Tage vor Buchungsbeginn</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={settings.reminder_days_before}
                      onChange={(e) => handleInputChange({ target: { name: 'reminder_days_before', value: parseInt(e.target.value) || 3 } })}
                      className="w-20"
                      data-testid="settings-reminder-days-input"
                    />
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">
                      Tage vor Beginn wird die Erinnerung gesendet
                    </span>
                  </div>
                  {!settings.smtp_email && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>SMTP muss konfiguriert sein, damit Erinnerungen gesendet werden.</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Follow-Up Email Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                Nachfass-E-Mail nach Buchungsende
              </CardTitle>
              <CardDescription>
                Automatisch eine E-Mail mit Feedback-Anfrage und Rabattcode senden, X Tage nachdem eine Buchung abgelaufen ist.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Nachfass-E-Mail aktivieren</Label>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Kunden erhalten automatisch Feedback-Anfrage + Rabattcode nach Buchungsende.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'follow_up_enabled', value: !settings.follow_up_enabled } })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.follow_up_enabled ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'
                  }`}
                  data-testid="settings-follow-up-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.follow_up_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {settings.follow_up_enabled && (
                <div className="pt-3 border-t border-[hsl(var(--border))] space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Tage nach Buchungsende</Label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={settings.follow_up_days_after}
                        onChange={(e) => handleInputChange({ target: { name: 'follow_up_days_after', value: parseInt(e.target.value) || 7 } })}
                        data-testid="settings-follow-up-days-input"
                      />
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        Wann die E-Mail nach Buchungsende gesendet wird
                      </span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Rabatt</Label>
                      <div className="flex items-center gap-2">
                        <select
                          value={settings.follow_up_discount_type}
                          onChange={(e) => handleInputChange({ target: { name: 'follow_up_discount_type', value: e.target.value } })}
                          className="rounded-md border border-[hsl(var(--border))] px-3 py-2 text-sm bg-white"
                          data-testid="settings-follow-up-discount-type"
                        >
                          <option value="percent">%</option>
                          <option value="fixed">CHF</option>
                        </select>
                        <Input
                          type="number"
                          min="0"
                          value={settings.follow_up_discount_value}
                          onChange={(e) => handleInputChange({ target: { name: 'follow_up_discount_value', value: parseFloat(e.target.value) || 10 } })}
                          className="w-20"
                          data-testid="settings-follow-up-discount-value"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Gültigkeit des Rabattcodes (Tage)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings.follow_up_code_validity_days}
                      onChange={(e) => handleInputChange({ target: { name: 'follow_up_code_validity_days', value: parseInt(e.target.value) || 30 } })}
                      className="w-32"
                      data-testid="settings-follow-up-validity-input"
                    />
                  </div>

                  {/* Manual Trigger */}
                  <div className="pt-3 border-t border-[hsl(var(--border))]">
                    <Label className="text-sm font-medium mb-2 block">Manuell auslösen</Label>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                      Sofort alle fälligen Nachfass-E-Mails senden (für Buchungen, deren Enddatum + X Tage bereits vergangen ist).
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleTriggerFollowUp}
                      disabled={triggeringFollowUp}
                      data-testid="settings-follow-up-trigger-button"
                    >
                      {triggeringFollowUp ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Jetzt auslösen
                    </Button>
                    {followUpResult && (
                      <Alert className={`mt-3 ${
                        followUpResult.type === 'success'
                          ? 'bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]'
                          : 'bg-[hsl(var(--destructive)/0.1)] border-[hsl(var(--destructive))]'
                      }`}>
                        <AlertDescription className={`text-sm ${
                          followUpResult.type === 'success'
                            ? 'text-[hsl(var(--chart-2))]'
                            : 'text-[hsl(var(--destructive))]'
                        }`}>
                          {followUpResult.message}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {!settings.smtp_email && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>SMTP muss konfiguriert sein, damit Nachfass-E-Mails gesendet werden.</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
    </>
  );
};

export default SettingsPreise;
